"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-8">
        {[1, 2, 3, 4, 5].map(i => <div key={i} data-index={i} className="h-5 w-16 bg-primary/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.slice(0, 5).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium tracking-wider uppercase transition-colors duration-200 hover:text-primary-dark hover:!bg-transparent focus:!bg-transparent focus-visible:outline-none focus-visible:ring-0">
                {item.label}
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-secondary to-primary transition-all duration-300 ease-out-back group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return null;
  }
  return <>
      <Button variant="ghost" size="icon" className="lg:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        {isOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {isOpen && <div className="absolute top-full left-0 right-0 bg-background/95 backdrop-blur-lg border-b border-border shadow-strong lg:hidden">
          <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-sm font-medium tracking-wider uppercase transition-colors duration-200 hover:text-primary-dark py-2" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-spring ${isScrolled ? "h-16 bg-background/80 backdrop-blur-lg border-b border-border shadow-soft" : "h-[90px] bg-transparent"}`}>
      <div className="container mx-auto h-full px-6 flex items-center justify-between">
        <Link to="/" className="flex-shrink-0 hover:opacity-80 transition-opacity duration-200">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549040/img/ecoluxmaya_logo.png" alt="EcoLuxMaya" className="h-7" />
        </Link>

        <MenuProvider menu_id="29" className="flex-1 flex justify-center">
          <HeaderMenuConsumer />
        </MenuProvider>

        <div className="flex items-center gap-4">
          <Button asChild className="hidden md:inline-flex relative overflow-hidden border border-secondary bg-transparent text-foreground hover:!bg-transparent focus:!bg-transparent group">
            <Link to={`https://wa.me/5219841234567?text=${encodeURIComponent("Hola  Quiero hacer una reservación para un tour. Fecha deseada: ____ Número de personas: ____ ¿Podrían confirmarme disponibilidad y precio final?")}`} newTab={isDesktop}>
              <span className="absolute inset-0 bg-gradient-to-r from-secondary to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative z-10 group-hover:text-foreground-secondary transition-colors duration-300 tracking-wider uppercase text-sm font-semibold">
                Book Now
              </span>
            </Link>
          </Button>

          <MenuProvider menu_id="29">
            <MobileMenuConsumer />
          </MenuProvider>
        </div>
      </div>
    </header>;
}